import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ExitItemsBrowser.css";
import { fetchFilters, fetchRows, fetchRawQuery } from "../../services/exitItemsBrowserService";

// Parse a mongosh-style (relaxed) or strict-JSON query into an object.
// Accepts unquoted keys and $operators, e.g. { is_wrong_exit: false, "exit_rule_data.atm_level.pcr": { $gt: 1 } }
const parseRelaxedQuery = (text) => {
  const t = (text || "").trim();
  if (!t) return {};
  try { return JSON.parse(t); } catch (_) { /* fall through to relaxed */ }
  const relaxed = t
    .replace(/'/g, '"')
    .replace(/([{,]\s*)([A-Za-z_$][\w$.]*)\s*:/g, '$1"$2":'); // quote bare keys
  return JSON.parse(relaxed);
};

const TEXT_COLS = new Set([
  "wrong_exit", "mp_trend", "date", "time", "index", "open_type", "entry_type",
  "iv_rule", "theta_rule", "vd60_rule", "vd30_rule", "vd10_rule",
  "opv30_rule", "opv60_rule", "opv10_rule", "oi_rule", "ce_vd_exit", "is_loss",
]);

const MultiSelect = ({ label, values, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const toggle = v => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange([...next]);
  };
  const selSet = new Set(selected);
  const filtered = values.filter(v => String(v).toLowerCase().includes(search.toLowerCase()));
  const btn = selected.length === 0 ? "Any" : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  return (
    <div className="eib-f" ref={rootRef}>
      <label>{label}</label>
      <div className="eib-ms">
        <button className="eib-ms-btn" type="button" onClick={e => { e.stopPropagation(); setOpen(!open); }}>{btn}</button>
        <div className={`eib-ms-pop${open ? " eib-open" : ""}`} onClick={e => e.stopPropagation()}>
          <input className="eib-ms-search" placeholder="search…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="eib-ms-tools">
            <button type="button" className="eib-ms-link" onClick={() => onChange([...values])}>select all</button>
            <button type="button" className="eib-ms-link" onClick={() => onChange([])}>clear</button>
          </div>
          <div>
            {filtered.map(v => (
              <label key={v} className="eib-ms-row">
                <input type="checkbox" checked={selSet.has(v)} onChange={() => toggle(v)} />
                {v}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const fmtCell = v => {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return <span className={v ? "eib-bool-t" : "eib-bool-f"}>{String(v)}</span>;
  if (typeof v === "number") {
    const s = Number.isInteger(v) ? v : (Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(3));
    return <span className={v < 0 ? "eib-neg" : v > 0 ? "eib-pos" : ""}>{s}</span>;
  }
  return String(v);
};

const compareVals = (a, b) => {
  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a === b) ? 0 : (a ? 1 : -1);
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const ExitItemsBrowser = () => {
  const [columns, setColumns] = useState([]);
  const [colOrder, setColOrder] = useState([]);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const dragFromRef = useRef(null);
  const dragMovedRef = useRef(false);
  const [dropTargetIdx, setDropTargetIdx] = useState(null);
  const [dropSide, setDropSide] = useState("before");

  const [optsExit, setOptsExit] = useState([]);
  const [optsIndex, setOptsIndex] = useState([]);
  const [optsOpt, setOptsOpt] = useState([]);
  const [optsEntry, setOptsEntry] = useState([]);

  const [exitCodeStr, setExitCodeStr] = useState("");
  const [selIndex, setSelIndex] = useState([]);
  const [selOpt, setSelOpt] = useState([]);
  const [selEntry, setSelEntry] = useState([]);
  const [wrong, setWrong] = useState("");
  const [loss, setLoss] = useState("");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [limit, setLimit] = useState(500);

  // raw MongoDB-style query mode (like entry_explorer.html)
  const [rawMode, setRawMode] = useState(false);
  const [rawQuery, setRawQuery] = useState(
    '{\n  "is_wrong_exit": false,\n  "option_type": "CE",\n  "exit_rule_data.atm_level.pcr": { "$gt": 2 }\n}'
  );

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [returned, setReturned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      let d;
      if (rawMode) {
        let q;
        try {
          q = parseRelaxedQuery(rawQuery);
        } catch (pe) {
          throw new Error("Query parse error: " + (pe.message || String(pe)));
        }
        d = await fetchRawQuery({ query: q, limit: Number(limit) || 500, dir: "desc" });
      } else {
        const exitCodeList = exitCodeStr.trim()
          ? exitCodeStr.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
          : [];
        d = await fetchRows({
          exit_code: exitCodeList,
          index_name: selIndex,
          option_type: selOpt,
          entry_type: selEntry,
          is_wrong_exit: wrong === "" ? null : wrong === "true",
          is_loss:       loss === ""  ? null : loss === "true",
          date_from: dfrom || null,
          date_to:   dto   || null,
          limit: Number(limit) || 500,
        });
      }
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setReturned(d.returned || 0);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [rawMode, rawQuery, exitCodeStr, selIndex, selOpt, selEntry, wrong, loss, dfrom, dto, limit]);

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchFilters();
        setColumns(d.columns || []);
        setOptsExit(d.exit_code || []);
        setOptsIndex(d.index_name || []);
        setOptsOpt(d.option_type || []);
        setOptsEntry(d.entry_type || []);
      } catch (e) {
        setErr(e.message || String(e));
      }
    })();
  }, []);

  useEffect(() => {
    setColOrder(columns);
  }, [columns]);

  useEffect(() => {
    if (columns.length > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.length]);

  const clearAll = () => {
    setExitCodeStr(""); setSelIndex([]); setSelOpt([]); setSelEntry([]);
    setWrong(""); setLoss(""); setDfrom(""); setDto(""); setLimit(500);
  };

  const { good, bad } = useMemo(() => ({
    good: rows.filter(r => r.wrong_exit === false).length,
    bad:  rows.filter(r => r.wrong_exit === true).length,
  }), [rows]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((ra, rb) => {
      const cmp = compareVals(ra[sortKey], rb[sortKey]);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const handleSort = (c) => {
    if (dragMovedRef.current) return;
    if (sortKey === c) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(c);
      setSortDir(TEXT_COLS.has(c) ? "asc" : "desc");
    }
  };

  const onDragStart = (e, i) => {
    dragFromRef.current = i;
    dragMovedRef.current = false;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) {}
  };

  const onDragOver = (e, i) => {
    if (dragFromRef.current === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    dragMovedRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    const side = (e.clientX - rect.left) < rect.width / 2 ? "before" : "after";
    if (dropTargetIdx !== i || dropSide !== side) {
      setDropTargetIdx(i);
      setDropSide(side);
    }
  };

  const onDrop = (e, i) => {
    e.preventDefault();
    const from = dragFromRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const side = (e.clientX - rect.left) < rect.width / 2 ? "before" : "after";
    setDropTargetIdx(null);
    setDropSide("before");
    dragFromRef.current = null;
    if (from === null) return;
    let to = i + (side === "after" ? 1 : 0);
    if (to > from) to -= 1;
    if (to === from) return;
    const next = [...colOrder];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setColOrder(next);
  };

  const onDragEnd = () => {
    dragFromRef.current = null;
    setDropTargetIdx(null);
    setTimeout(() => { dragMovedRef.current = false; }, 0);
  };

  const resetView = () => {
    setColOrder(columns);
    setSortKey("date");
    setSortDir("desc");
  };

  return (
    <div className="eib-root">
      <div className="eib-filters">
        <div className="eib-f">
          <label>Exit code</label>
          <input
            type="text"
            list="eib-exit-code-opts"
            placeholder="e.g. PE_LST_IV7"
            value={exitCodeStr}
            onChange={e => setExitCodeStr(e.target.value)}
          />
          <datalist id="eib-exit-code-opts">
            {optsExit.map(v => <option key={v} value={v} />)}
          </datalist>
        </div>
        <MultiSelect label="Index / Symbol" values={optsIndex} selected={selIndex} onChange={setSelIndex} />
        <MultiSelect label="Option type"    values={optsOpt}   selected={selOpt}   onChange={setSelOpt}   />
        <MultiSelect label="Entry type"     values={optsEntry} selected={selEntry} onChange={setSelEntry} />
        <div className="eib-f"><label>Wrong exit</label>
          <select value={wrong} onChange={e => setWrong(e.target.value)}>
            <option value="">any</option><option value="true">true</option><option value="false">false</option>
          </select>
        </div>
        <div className="eib-f"><label>Is loss</label>
          <select value={loss} onChange={e => setLoss(e.target.value)}>
            <option value="">any</option><option value="true">true</option><option value="false">false</option>
          </select>
        </div>
        <div className="eib-f"><label>Date from</label><input type="date" value={dfrom} onChange={e => setDfrom(e.target.value)} /></div>
        <div className="eib-f"><label>Date to</label><input type="date" value={dto} onChange={e => setDto(e.target.value)} /></div>
        <div className="eib-f"><label>Limit</label><input type="number" min="1" max="10000" value={limit} onChange={e => setLimit(e.target.value)} /></div>
        <div className="eib-f"><label>&nbsp;</label><button className="eib-btn" onClick={load}>Apply</button></div>
        <div className="eib-f"><label>&nbsp;</label><button className="eib-btn sec" onClick={clearAll}>Clear</button></div>
        <div className="eib-f"><label>&nbsp;</label>
          <button className={`eib-btn ${rawMode ? "" : "sec"}`} type="button" onClick={() => setRawMode(m => !m)}>
            {rawMode ? "✓ Mongo query" : "Mongo query"}
          </button>
        </div>
      </div>

      {rawMode && (
        <div className="eib-raw">
          <div className="eib-raw-head">
            <span className="eib-raw-title">Raw MongoDB query</span>
            <span className="eib-raw-hint">
              filters the <code>exit_items</code> collection by stored field paths — e.g.{" "}
              <code>exit_unique_code</code>, <code>is_wrong_exit</code>, <code>only_date</code>,{" "}
              <code>exit_rule_data.atm_level.pcr</code>. Mongo operators ($gt, $in, $or, $regex…) work.
              Structured filters above are ignored while this is on.
            </span>
          </div>
          <textarea
            className="eib-raw-box"
            spellCheck={false}
            value={rawQuery}
            onChange={e => setRawQuery(e.target.value)}
            placeholder='{ "exit_unique_code": "CE_NE_LST_RE_01", "is_wrong_exit": false }'
          />
          <div className="eib-raw-actions">
            <button className="eib-btn" onClick={load}>Run query</button>
            <span className="eib-raw-examples">examples:
              <button type="button" className="eib-ms-link" onClick={() => setRawQuery('{\n  "exit_unique_code": { "$regex": "_RE_", "$options": "i" }\n}')}>RE rules</button>
              <button type="button" className="eib-ms-link" onClick={() => setRawQuery('{\n  "is_loss": true,\n  "index_name": "NIFTY 50"\n}')}>losses</button>
              <button type="button" className="eib-ms-link" onClick={() => setRawQuery('{\n  "option_type": "CE",\n  "exit_rule_data.support_weak.pcr": { "$gt": 3.5 }\n}')}>strong put-wall</button>
            </span>
          </div>
        </div>
      )}

      <div className="eib-meta">
        {err
          ? <span className="eib-err">Error: {err}</span>
          : loading
            ? <span className="eib-loading">Loading…</span>
            : <>
                <span className="eib-pill">{returned}/{total}</span>
                <span>good: <b className="eib-good-n">{good}</b></span>
                <span>wrong: <b className="eib-bad-n">{bad}</b></span>
                <span className="eib-hint">click header to sort · drag header to reorder</span>
              </>}
        <button className="eib-reset-link" onClick={resetView} title="Reset column order & sort">Reset view</button>
      </div>

      <div className="eib-wrap">
        <table className="eib-table">
          <thead>
            <tr>
              {colOrder.map((c, i) => {
                const isFirst = i === 0;
                const isTxt = TEXT_COLS.has(c);
                const sorted = sortKey === c;
                const isDropTarget = dropTargetIdx === i;
                const cls = [
                  isFirst ? "eib-first" : "",
                  isTxt ? "eib-txt" : "",
                  sorted ? "eib-sorted" : "",
                  isDropTarget ? `eib-drop-${dropSide}` : "",
                ].filter(Boolean).join(" ");
                return (
                  <th
                    key={c}
                    draggable
                    onDragStart={(e) => onDragStart(e, i)}
                    onDragOver={(e) => onDragOver(e, i)}
                    onDrop={(e) => onDrop(e, i)}
                    onDragEnd={onDragEnd}
                    onClick={() => handleSort(c)}
                    title={`${c} — click to sort · drag to reorder`}
                    className={cls}
                  >
                    <span className="eib-th-inner">
                      <span className="eib-th-label">{c}</span>
                      <span className="eib-sort-ind">{sorted ? (sortDir === "asc" ? "▲" : "▼") : "⇵"}</span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r, ri) => {
              const cls = r.wrong_exit === true ? "eib-wrong" : r.wrong_exit === false ? "eib-good" : "";
              return (
                <tr key={ri} className={cls}>
                  {colOrder.map((c, i) => (
                    <td key={c} className={`${i === 0 ? "eib-first " : ""}${TEXT_COLS.has(c) ? "eib-txt" : ""}`}>{fmtCell(r[c])}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExitItemsBrowser;
